import React, { useEffect, useMemo, useState } from "react";
import { CarouselItem } from "./";
import { Indicators } from "./Indicators";
import { sanitizeProps, useInterval } from "./util";
import {
  StyledButtonWrapper,
  StyledIconButton,
  StyledItemWrapper,
  StyledRoot,
} from "./Styled";

interface CarouselProps {
  children?: React.ReactElement | React.ReactElement[];
  sx?: Record<string, any>;
  className?: string;
  height?: string | number;
  index?: number;
  strictIndexing?: boolean;
  autoPlay?: boolean;
  stopAutoPlayOnHover?: boolean;
  interval?: number;
  animation?: "slide" | "fade";
  duration?: number;
  swipe?: boolean;
  navButtonsAlwaysInvisible?: boolean;
  navButtonsAlwaysVisible?: boolean;
  cycleNavigation?: boolean;
  fullHeightHover?: boolean;
  navButtonsWrapperProps?: Record<string, any>;
  navButtonsProps?: Record<string, any>;
  NavButton?: React.ComponentType<any>;
  NextIcon?: React.ReactElement;
  PrevIcon?: React.ReactElement;
  indicators?: boolean;
  indicatorContainerProps?: Record<string, any>;
  indicatorIconButtonProps?: Record<string, any>;
  activeIndicatorIconButtonProps?: Record<string, any>;
  IndicatorIcon?: React.ReactElement | React.ReactElement[];
  onChange?: (index: number, prevIndex: number) => void;
  changeOnFirstRender?: boolean;
  next?: (index: number, prevIndex: number) => void;
  prev?: (index: number, prevIndex: number) => void;
}

export const Carousel = (props: CarouselProps) => {
  const [state, setState] = useState({
    active: 0,
    prevActive: 0,
    next: true,
  });

  // Used to set carousel's height. It is being set by the CarouselItems
  const [childrenHeight, setChildrenHeight] = useState<number | undefined>();
  const [paused, setPaused] = useState(false);

  const sanitizedProps = useMemo(() => sanitizeProps(props), [props]);

  // componentDidMount & onIndexChange
  const next = (event?: any) => {
    const { children, cycleNavigation } = sanitizedProps;
    const last = Array.isArray(children) ? children.length - 1 : 0;
    const nextActive =
      state.active + 1 > last
        ? cycleNavigation
          ? 0
          : state.active
        : state.active + 1;

    setNext(nextActive, true);
    if (event) event.stopPropagation();
  };

  const prev = (event?: React.SyntheticEvent) => {
    const { children, cycleNavigation } = sanitizedProps;
    const last = Array.isArray(children) ? children.length - 1 : 0;
    const nextActive =
      state.active - 1 < 0
        ? cycleNavigation
          ? last
          : state.active
        : state.active - 1;

    setNext(nextActive, false);
    if (event) event.stopPropagation();
  };

  const setNext = (index: number, isNext: boolean, runCallbacks = true) => {
    const { onChange, children, strictIndexing } = sanitizedProps;

    if (Array.isArray(children)) {
      if (strictIndexing && index > children.length - 1)
        index = children.length - 1;
      if (strictIndexing && index < 0) index = 0;
    } else {
      index = 0;
    }

    if (runCallbacks) {
      if (isNext !== undefined) {
        isNext
          ? sanitizedProps.next(index, state.active)
          : sanitizedProps.prev(index, state.active);
      }
      onChange(index, state.active);
    }

    if (isNext === undefined) {
      isNext = index > state.active;
    }

    setState({
      active: index,
      prevActive: state.active,
      next: isNext,
    });
  };

  useEffect(() => {
    const { index, changeOnFirstRender } = sanitizedProps;
    setNext(index, true, changeOnFirstRender);
  }, [sanitizedProps.index, sanitizedProps.changeOnFirstRender]);

  useInterval(() => {
    const { autoPlay } = sanitizedProps;
    if (autoPlay && !paused) {
      next();
    }
  }, sanitizedProps.interval);

  const {
    children,
    sx,
    className,
    height,
    stopAutoPlayOnHover,
    animation,
    duration,
    swipe,
    navButtonsAlwaysInvisible,
    navButtonsAlwaysVisible,
    cycleNavigation,
    fullHeightHover,
    navButtonsProps,
    navButtonsWrapperProps,
    NavButton,
    NextIcon,
    PrevIcon,
    indicators,
    indicatorContainerProps,
    indicatorIconButtonProps,
    activeIndicatorIconButtonProps,
    IndicatorIcon,
  } = sanitizedProps;

  const showButton = (nextBtn = true) => {
    if (cycleNavigation) return true;
    const last = Array.isArray(children) ? children.length - 1 : 0;
    if (nextBtn && state.active === last) return false;
    return nextBtn || state.active !== 0;
  };

  return (
    <StyledRoot
      sx={sx}
      className={className}
      onMouseOver={() => {
        stopAutoPlayOnHover && setPaused(true);
      }}
      onMouseOut={() => {
        stopAutoPlayOnHover && setPaused(false);
      }}
      onFocus={() => {
        stopAutoPlayOnHover && setPaused(true);
      }}
      onBlur={() => {
        stopAutoPlayOnHover && setPaused(false);
      }}
    >
      <StyledItemWrapper style={{ height: height || childrenHeight }}>
        {Array.isArray(children) ? (
          children.map((child, index) => (
            <CarouselItem
              key={`carousel-item${index}`}
              state={state}
              index={index}
              maxIndex={children.length - 1}
              child={child}
              animation={animation}
              duration={duration}
              swipe={swipe}
              next={next}
              prev={prev}
              height={height}
              setHeight={setChildrenHeight}
            />
          ))
        ) : (
          <CarouselItem
            key="carousel-item0"
            state={state}
            index={0}
            maxIndex={0}
            child={children}
            animation={animation}
            duration={duration}
            swipe={swipe}
            next={next}
            prev={prev}
            height={height}
            setHeight={setChildrenHeight}
          />
        )}
      </StyledItemWrapper>

      {!navButtonsAlwaysInvisible && showButton(true) && (
        <StyledButtonWrapper
          $next
          $prev={false}
          $fullHeightHover={fullHeightHover}
          {...navButtonsWrapperProps}
        >
          {NavButton ? (
            NavButton({
              onClick: next,
              next: true,
              prev: false,
              ...navButtonsProps,
            })
          ) : (
            <StyledIconButton
              $alwaysVisible={navButtonsAlwaysVisible}
              onClick={next}
              aria-label="Next"
              {...navButtonsProps}
            >
              {NextIcon}
            </StyledIconButton>
          )}
        </StyledButtonWrapper>
      )}

      {!navButtonsAlwaysInvisible && showButton(false) && (
        <StyledButtonWrapper
          $next={false}
          $prev
          $fullHeightHover={fullHeightHover}
          {...navButtonsWrapperProps}
        >
          {NavButton ? (
            NavButton({
              onClick: prev,
              next: false,
              prev: true,
              ...navButtonsProps,
            })
          ) : (
            <StyledIconButton
              $alwaysVisible={navButtonsAlwaysVisible}
              onClick={prev}
              aria-label="Previous"
              {...navButtonsProps}
            >
              {PrevIcon}
            </StyledIconButton>
          )}
        </StyledButtonWrapper>
      )}

      {indicators && (
        <Indicators
          length={Array.isArray(children) ? children.length : 0}
          active={state.active}
          press={(index) => setNext(index, true)}
          indicatorContainerProps={indicatorContainerProps}
          indicatorIconButtonProps={indicatorIconButtonProps}
          activeIndicatorIconButtonProps={activeIndicatorIconButtonProps}
          IndicatorIcon={IndicatorIcon}
        />
      )}
    </StyledRoot>
  );
};
